import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

interface Event {
  id: string;
  title: string;
  category: string;
  start_date: string;
  location_city: string | null;
  location_country: string | null;
  location_type: string;
  cover_image: string | null;
  price: number | null;
}

const CATEGORIES = ['All', 'Music', 'Tech', 'Art', 'Sports', 'Food', 'Business', 'Health', 'Education'];

export default function FreeEventsPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [registeredIds, setRegisteredIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  const firstName = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || null;

  useEffect(() => {
    document.title = 'Free Events Near You | EVENTSHOWCASE';
    fetchFreeEvents();

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Free Events Near You',
      description: 'Discover amazing free events happening near you. No tickets, no fees — just great experiences.',
      url: `${SITE_URL}/events/free`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'EVENTSHOWCASE',
        url: SITE_URL,
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: events.map((event, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Event',
            name: event.title,
            startDate: event.start_date,
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: event.location_type === 'online' 
              ? 'https://schema.org/OnlineEventAttendanceMode' 
              : 'https://schema.org/OfflineEventAttendanceMode',
            location: event.location_type === 'online'
              ? { '@type': 'VirtualLocation', url: `${SITE_URL}/event/${event.id}` }
              : { '@type': 'Place', name: [event.location_city, event.location_country].filter(Boolean).join(', ') || 'TBA' },
            offers: {
              '@type': 'Offer',
              price: 0,
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
            },
          },
        })),
      },
    };

    const existing = document.getElementById('schema-free-events');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-free-events';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-free-events');
      if (el) el.remove();
    };
  }, [events]);

  useEffect(() => {
    if (user) fetchRegisteredIds();
  }, [user]);

  const fetchFreeEvents = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('events')
      .select('id, title, category, start_date, location_city, location_country, location_type, cover_image, price')
      .eq('is_published', true)
      .or('price.is.null,price.eq.0')
      .gte('start_date', new Date().toISOString())
      .order('start_date', { ascending: true })
      .limit(24);
    setEvents(data || []);
    setLoading(false);
  };

  const fetchRegisteredIds = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('registrations')
      .select('event_id')
      .eq('user_id', user.id);
    if (data) setRegisteredIds(new Set(data.map((r: any) => r.event_id)));
  };

  const filtered = activeCategory === 'All' ? events : events.filter(e => e.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar forceScrolled />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-bold uppercase tracking-wide mb-5">
            <i className="ri-gift-line"></i> 100% Free to Attend
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {firstName ? `${firstName}'s Free Events` : 'Free Events'}
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            {firstName
              ? `Hey ${firstName}! Here are free events you can attend — no tickets, no fees.`
              : 'Discover amazing free events happening near you. No tickets, no fees — just great experiences.'}
          </p>
          {user && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white border border-teal-200 rounded-full px-4 py-2 text-sm text-teal-700">
              <i className="ri-ticket-line"></i>
              <span>{registeredIds.size} event{registeredIds.size !== 1 ? 's' : ''} already registered</span>
              <Link to="/dashboard/my-registrations" className="font-semibold underline underline-offset-2 hover:text-teal-900 whitespace-nowrap">View all</Link>
            </div>
          )}
        </div>
      </section>

      {/* Category filter */}
      <section className="sticky top-16 z-30 bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeCategory === cat ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Events grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No free events found</h3>
            <p className="text-gray-500 text-sm mb-5">Check back soon or browse all events</p>
            <Link to="/discover" className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">
              Browse All Events
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-6">{filtered.length} free event{filtered.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map(event => {
                const isRegistered = registeredIds.has(event.id);
                return (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/event/${event.id}`)}
                    className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-teal-200 transition-all cursor-pointer group"
                  >
                    <div className="relative h-44 bg-gray-100 overflow-hidden">
                      {event.cover_image ? (
                        <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-3xl text-gray-300"></i>
                        </div>
                      )}
                      <span className="absolute top-2 left-2 bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">FREE</span>
                      {isRegistered && (
                        <span className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-check-line text-[10px]"></i> Registered
                        </span>
                      )}
                      {!isRegistered && (
                        <span className="absolute top-2 right-2 bg-white/90 text-gray-700 text-[10px] font-medium px-2 py-0.5 rounded-full">{event.category}</span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-sm text-gray-900 mb-2 line-clamp-2">{event.title}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <i className="ri-calendar-line"></i>
                        <span>{new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      {(event.location_city || event.location_type === 'online') && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <i className={event.location_type === 'online' ? 'ri-global-line' : 'ri-map-pin-line'}></i>
                          <span>{event.location_type === 'online' ? 'Online' : [event.location_city, event.location_country].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* Logged-out CTA */}
      {!user && (
        <section className="bg-teal-50 border-t border-teal-100 py-10 px-4 text-center">
          <p className="text-gray-700 font-medium mb-3">Sign up to track your registrations and get event reminders</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/signup" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap">
              Create Free Account
            </Link>
            <Link to="/login" className="inline-flex items-center gap-2 border border-gray-200 text-gray-700 hover:bg-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap">
              Log In
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
